import {
  ArrowDownAZ,
  ArrowUpAZ,
  CalendarArrowDown,
  CalendarArrowUp,
  ListOrdered,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type SortField = 'default' | 'name' | 'date';
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface SortButtonProps {
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

const sortOptions = [
  {
    field: 'default' as const,
    direction: 'asc' as const,
    label: 'Default',
    icon: ListOrdered,
  },
  {
    field: 'name' as const,
    direction: 'asc' as const,
    label: 'Name A-Z',
    icon: ArrowDownAZ,
  },
  {
    field: 'name' as const,
    direction: 'desc' as const,
    label: 'Name Z-A',
    icon: ArrowUpAZ,
  },
  {
    field: 'date' as const,
    direction: 'desc' as const,
    label: 'Newest first',
    icon: CalendarArrowDown,
  },
  {
    field: 'date' as const,
    direction: 'asc' as const,
    label: 'Oldest first',
    icon: CalendarArrowUp,
  },
];

export const SortButton = ({ sortConfig, onSortChange }: SortButtonProps) => {
  const currentOption = sortOptions.find(
    (opt) =>
      opt.field === sortConfig.field && opt.direction === sortConfig.direction,
  );
  const CurrentIcon = currentOption?.icon ?? ListOrdered;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <CurrentIcon className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Sort: {currentOption?.label}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="w-36">
        {sortOptions.map((option) => {
          const Icon = option.icon;
          const isActive =
            sortConfig.field === option.field &&
            sortConfig.direction === option.direction;
          return (
            <DropdownMenuItem
              key={`${option.field}-${option.direction}`}
              onClick={() =>
                onSortChange({
                  field: option.field,
                  direction: option.direction,
                })
              }
              className={cn('text-xs gap-2', isActive && 'bg-accent')}
            >
              <Icon className="h-3 w-3" />
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
